import { useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Atom, Plus, Brain, Cpu, Zap, Cloud } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { listByoLlmProviders, listByoLlmKeys, type ByoLlmProvider, type ByoLlmKey } from '@/lib/api';

const PROVIDERS = [
  { id: 'anthropic', name: 'Anthropic Claude (direct)', auth: 'bearer · API key', maxCtx: 1_000_000, streaming: true, toolUse: true, promptCache: true, logo: '🅐', color: 'from-orange-500/40 to-amber-500/20', region: 'US/EU' },
  { id: 'aws_bedrock_anthropic', name: 'AWS Bedrock - Anthropic', auth: 'IAM · SigV4', maxCtx: 1_000_000, streaming: true, toolUse: true, promptCache: true, logo: '🅑', color: 'from-yellow-500/40 to-orange-500/20', region: 'AWS regions' },
  { id: 'gcp_vertex_anthropic', name: 'GCP Vertex - Anthropic', auth: 'GCP OAuth2', maxCtx: 1_000_000, streaming: true, toolUse: true, promptCache: true, logo: '🅥', color: 'from-blue-500/40 to-cyan-500/20', region: 'GCP regions' },
  { id: 'azure_openai', name: 'Azure OpenAI', auth: 'Azure key', maxCtx: 200_000, streaming: true, toolUse: true, promptCache: false, logo: '🅐', color: 'from-blue-500/40 to-sky-500/20', region: 'Azure regions' },
  { id: 'openai', name: 'OpenAI (direct)', auth: 'bearer · API key', maxCtx: 200_000, streaming: true, toolUse: true, promptCache: false, logo: '🅞', color: 'from-emerald-500/40 to-green-500/20', region: 'US' },
  { id: 'mistral', name: 'Mistral La Plateforme', auth: 'bearer · API key', maxCtx: 128_000, streaming: true, toolUse: true, promptCache: false, logo: '🅜', color: 'from-orange-500/40 to-red-500/20', region: 'EU (FR)' },
  { id: 'cohere', name: 'Cohere', auth: 'bearer · API key', maxCtx: 128_000, streaming: true, toolUse: true, promptCache: false, logo: '🅒', color: 'from-pink-500/40 to-rose-500/20', region: 'US/EU' },
  { id: 'on_prem_vllm', name: 'On-prem vLLM (OpenAI-compat)', auth: 'bearer · API key', maxCtx: 128_000, streaming: true, toolUse: false, promptCache: false, logo: '🅥', color: 'from-purple-500/40 to-violet-500/20', region: 'Customer VPC' },
  { id: 'custom_openai_compat', name: 'Custom OpenAI-compatible', auth: 'custom', maxCtx: 128_000, streaming: true, toolUse: false, promptCache: false, logo: '🅒', color: 'from-slate-500/40 to-gray-500/20', region: 'Customer-defined' },
];

interface ProviderView {
  id: string;
  name: string;
  auth: string;
  maxCtx: number;
  streaming: boolean;
  toolUse: boolean;
  promptCache: boolean;
  logo: string;
  color: string;
  region: string;
}

function adaptProvider(p: ByoLlmProvider): ProviderView {
  // Pull through visual hints from the static catalogue when ids match,
  // otherwise synthesise a neutral card.
  const fallback = PROVIDERS.find((x) => x.id === p.id);
  return {
    id: p.id,
    name: p.displayName,
    auth: p.authMethod,
    maxCtx: p.maxContextWindowTokens,
    streaming: p.supportsStreaming,
    toolUse: p.supportsToolUse,
    promptCache: p.supportsPromptCaching,
    logo: fallback?.logo ?? '🅛',
    color: fallback?.color ?? 'from-slate-500/40 to-gray-500/20',
    region: fallback?.region ?? '-',
  };
}

export function FrontierByoLlm() {
  const tx = useRegulationText();
  const [liveProviders, setLiveProviders] = useState<ProviderView[] | null>(null);
  const [registered, setRegistered] = useState<ByoLlmKey[]>([]);
  useEffect(() => {
    let cancelled = false;
    listByoLlmProviders()
      .then((res) => {
        if (cancelled || !res.providers.length) return;
        setLiveProviders(res.providers.map(adaptProvider));
      })
      .catch(() => {
        /* keep mocks */
      });
    listByoLlmKeys(20)
      .then((res) => {
        if (cancelled) return;
        setRegistered(res.keys);
      })
      .catch(() => {
        /* keep empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const providers = liveProviders ?? PROVIDERS;
  return (
    <PageContainer>
      <PageHeader
        title="Tier 5 BYO-LLM"
        subtitle={tx("Customer brings its own LLM for the most ambiguous PII classifications, breach narrative drafting, rule recommendations, and compliance-replay synthesis. Keys live in customer-VPC KMS.")}
        badges={[
          { label: `${providers.length} providers`, variant: 'glass' },
          { label: liveProviders ? 'live catalogue' : 'KMS-only keys', variant: liveProviders ? 'info' : 'success' },
        ]}
        actions={<Button size="sm"><Plus className="h-4 w-4" /> Register provider key</Button>}
      />

      {registered.length > 0 && (
        <>
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Registered keys for this tenant</h2>
          <GlassCard className="p-0 overflow-hidden mb-6">
            <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
              <div className="col-span-3">Provider</div>
              <div className="col-span-3">Model</div>
              <div className="col-span-3">Purpose</div>
              <div className="col-span-1">Ctx</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {registered.map((k, i) => (
              <motion.div
                key={k.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="grid grid-cols-12 px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors items-center"
              >
                <div className="col-span-3 font-mono text-xs">{k.provider}</div>
                <div className="col-span-3 font-mono text-xs">{k.modelId}</div>
                <div className="col-span-3 text-xs">
                  <code>{k.purposeTag}</code>
                </div>
                <div className="col-span-1 text-[11px] tabular">
                  {(k.contextWindowTokens / 1000).toLocaleString('en-IN')}K
                </div>
                <div className="col-span-2 text-right">
                  <Badge variant={k.status === 'active' ? 'success' : 'warning'} className="text-[9px]">
                    {k.status}
                  </Badge>
                </div>
              </motion.div>
            ))}
          </GlassCard>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
          >
            <GlassCard shine className="p-5 h-full">
              <div className={`h-24 -m-5 mb-3 rounded-t-lg bg-gradient-to-br ${p.color} flex items-center justify-center text-5xl relative overflow-hidden`}>
                <span className="opacity-90">{p.logo}</span>
                <div className="absolute inset-0 mesh-grid opacity-20" />
              </div>
              <CardTitle className="text-sm leading-tight">{p.name}</CardTitle>
              <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                <Cloud className="h-3 w-3" />
                <span>{p.region}</span>
              </div>

              <div className="space-y-1.5 mt-3 pt-3 border-t border-border/30 text-[11px]">
                <CapabilityRow icon={Brain} label="Max context" value={`${(p.maxCtx / 1000).toLocaleString('en-IN')}K tokens`} />
                <CapabilityRow icon={Zap} label="Streaming" value={p.streaming ? '✓' : '-'} good={p.streaming} />
                <CapabilityRow icon={Cpu} label="Tool use" value={p.toolUse ? '✓' : '-'} good={p.toolUse} />
                <CapabilityRow icon={Atom} label="Prompt caching" value={p.promptCache ? '✓' : '-'} good={p.promptCache} />
                <CapabilityRow icon={Cloud} label="Auth" value={p.auth} />
              </div>

              <Button variant="glass" size="sm" className="w-full mt-3">Register key</Button>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </PageContainer>
  );
}

function CapabilityRow({ icon: Icon, label, value, good }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; good?: boolean }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </span>
      <span className={good ? 'text-success font-medium' : 'tabular'}>{value}</span>
    </div>
  );
}
